import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {StringTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {getLibraryDefinitionTypedValue} from 'procedures/parameter-library/libraryParameterDefinitionTypedValueUtils';
import {Optional} from 'util/Types';

/**
 * Custom evals for the Description and Unit columns of tables whose rows are driven by a Parameter Library
 * definition. In both, `cellDependencies[0]` is the Parameter column's typed value, which carries the library
 * definition id.
 */

/**
 * Computes the read-only Description cell from the selected library parameter. The description text is loaded via
 * custom resolution; with no library parameter selected the cell is empty.
 */
export function libraryParameterDescriptionCustomEval(
  descriptionTypedValue: StringTypedValue,
  _columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  _customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn,
  _resolveTypedValueToType: ResolveTypedValueToTypeFn,
  _fileId?: string | null
): CustomEvalResult {
  const libDefinitionTypedValue = getLibraryDefinitionTypedValue(cellDependencies[0]);
  if (libDefinitionTypedValue == null) {
    return {
      val: {type: BaseTypedValueType.STRING as 'STRING', rawValue: ''},
      error: null,
      locked: true,
    };
  }
  const {resolvedValue, error} = resolveFromResolutionQuery(
    descriptionTypedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType: CustomResolutionQueryType.LIBRARY_PARAMETER_DEFINITION_DESCRIPTION,
      libraryParameterDefinitionId: libDefinitionTypedValue.id,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: true};
}

/**
 * Validates that the selected unit belongs to the unit type defined on the selected library parameter. A parameter
 * without a unit type accepts any unit; a parameter with one rejects an empty unit. With no library parameter
 * selected there is nothing to validate against.
 *
 * Custom evals run before typed value resolution, so text or formula input (e.g. a pasted "mg") hasn't been
 * resolved to a unit reference yet. It is resolved first so the unit id sent for validation is the real one.
 */
export function libraryParameterUnitCustomEval(
  typedValue: TypedValue,
  columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  _customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn,
  resolveTypedValueToType: ResolveTypedValueToTypeFn,
  _fileId?: string | null
): CustomEvalResult {
  const libraryDefinitionTypedValue = getLibraryDefinitionTypedValue(cellDependencies[0]);
  if (libraryDefinitionTypedValue == null) {
    return {val: typedValue, error: null, locked: false};
  }

  let unitTypedValue = typedValue;
  if (columnDefinition != null && !isTypedValueEmpty(typedValue)) {
    const {resolvedValue, error: resolutionError} = resolveTypedValueToType(columnDefinition, typedValue);
    if (resolutionError != null) {
      return {val: resolvedValue ?? typedValue, error: resolutionError, locked: false};
    }
    unitTypedValue = resolvedValue ?? typedValue;
  }

  const unitId =
    unitTypedValue.type === BaseTypedValueType.UNIT_REFERENCE ? unitTypedValue.unitSummary.id : null;

  const {resolvedValue, error} = resolveFromResolutionQuery(
    unitTypedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType: CustomResolutionQueryType.PROCEDURE_PARAMETER_VALIDATE_UNIT_TYPE,
      libraryParameterDefinitionId: libraryDefinitionTypedValue.id,
      unitId,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue ?? unitTypedValue, error, locked: false};
}
